import { useEffect, useMemo, useRef } from 'react';
import { useGetSlotDetailQuery } from 'common/api/proctor';
import { formattedDateTime, formattedTime } from 'common/utils/DateTimeUtils';
import { useAppDispatch } from 'app/hooks';
import { updateBreadcrumb } from 'common/slice/breadcrumbsSlice';
import { setActiveAttendee } from 'common/slice/activeAttendeeSlice';
import Header from 'common/components/Header';
import Loader from 'common/components/Loader';
import Session from './Session';
import ActionModal from 'common/components/ActionModal';
import { startSession, stopSession } from 'common/slice/sessionListSlice';
import { Mixpanel } from '../../Mixpanel';
import { Attendee } from 'common/api/proctorType';

const SlotDetail = () => {
  const dispatch = useAppDispatch();
  const urlParams = new URLSearchParams(window.location.search);
  const event_id = +urlParams.get('event_id');
  const slot_id = +urlParams.get('slot_id');

  const { data, isSuccess, isLoading } = useGetSlotDetailQuery({
    event_id,
    slot_id,
  });
  const slotIdentityRef = useRef<string | null>(null);
  const defaultAttendeeIdRef = useRef<number | null>(null);

  const stableAttendees = useStableAttendees(data?.attendees);

  useEffect(() => {
    if (!data) {
      return;
    }
    const slotIdentity = `${event_id}_${slot_id}`;
    const isNewSlot = slotIdentityRef.current !== slotIdentity;
    if (isNewSlot) {
      slotIdentityRef.current = slotIdentity;
      defaultAttendeeIdRef.current = null;
      Mixpanel.track('proview_console_tile_view_page_opened');
      const url = `/slot?event_id=${event_id}&slot_id=${slot_id}`;
      const label = `${formattedDateTime(data?.slot_starts_at)} | ${formattedTime(
        data?.slot_starts_at,
      )} - ${formattedTime(data?.slot_ends_at)}`;
      const payload = [
        {
          label,
          url,
        },
      ];
      dispatch(updateBreadcrumb(payload));
    }

    const defaultAttendee = data.attendees?.[0];
    if (defaultAttendee && defaultAttendeeIdRef.current !== defaultAttendee.attendee_id) {
      defaultAttendeeIdRef.current = defaultAttendee.attendee_id;
      dispatch(setActiveAttendee({ ...defaultAttendee, isDetailView: false }));
    }
  }, [data, dispatch, event_id, slot_id]);

  const participantGroupId = data?.participant_group_id;

  useEffect(() => {
    if (!participantGroupId) return;
    dispatch(startSession(participantGroupId));
    return () => {
      dispatch(stopSession(participantGroupId));
    };
  }, [dispatch, participantGroupId]);
  
  return (
    <>
      {isLoading && <Loader />}
      {isSuccess && (
        <div className="flex flex-col w-screen h-screen">
          <Header />
          <Session attendees={stableAttendees} participant_group_id={data?.participant_group_id} />
          <ActionModal />
        </div>
      )}
    </>
  );
};

export default SlotDetail;

const EMPTY_ATTENDEE_LIST: Attendee[] = [];

const haveAttendeesChanged = (prev: Attendee[] = [], next: Attendee[] = []) => {
  if (prev.length !== next.length) {
    return true;
  }
  for (let index = 0; index < next.length; index += 1) {
    if (prev[index]?.attendee_id !== next[index]?.attendee_id) {
      return true;
    }
  }
  return false;
};

const useStableAttendees = (attendees: Attendee[] = []) => {
  const attendeesRef = useRef<Attendee[]>([]);

  return useMemo(() => {
    if (!attendees?.length) {
      attendeesRef.current = EMPTY_ATTENDEE_LIST;
      return attendeesRef.current;
    }
    if (haveAttendeesChanged(attendeesRef.current, attendees)) {
      attendeesRef.current = attendees;
    }
    return attendeesRef.current;
  }, [attendees]);
};
